// src/components/BotSettings/KeywordTriggersRow.js
// @flow

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';

import SPBackend from '../../services/SPBackend';
import { log } from '../../utils/jsUtils';
import { useI18n } from '../../contexts/I18nContext';
import botSettingsContentStyles from './Styles/BotSettingsContent.Style';

// If you have Flow types for Keyword, define them:
// import type { Keyword } from '../../services/SPBackend';

type Props = {
  keywords: Array<any>,
  setKeywords: Function,
  setKeyword: Function,
  keyword: any,
  setOpenEditKeywordDialog: Function,
  setIsNewKeyword: Function
};

const KeywordTriggersRow = (props: Props) => {
  const {
    keywords = [],
    setKeywords,
    setKeyword,
    keyword,
    setOpenEditKeywordDialog,
    setIsNewKeyword
  } = props;

  const theme = useTheme();
  const dispatch = useDispatch();
  const i18n = useI18n();
  
  // Pull the real user object from Redux
  const user = useSelector((state) => state.user.user);

  const [checkedState, setCheckedState] = useState(false);

  // Toggle the "IsActive" state of a given keyword
  const handleSwitchChange = (event, kwd) => {
    setCheckedState(event.target.checked);
    log('[handleSwitchChange] toggling', event.target.checked, kwd);

    const keywordPayload = {
      ...kwd,
      IsActive: event.target.checked
    };

    dispatch({
      type: 'ACTION_SET_IS_LOADING_MODULE',
      module: 'removeKeywordTrigger',
      loading: true
    });

    SPBackend.editKeywordTrigger(keywordPayload, user, false)
      .then((response) => {
        log('[handleAddNewKeyword] response:', response.data);
        const updatedKeywords = response.data;  // array of keywords
        setKeywords(updatedKeywords);

        dispatch({
          type: 'ACTION_SET_SNACK_MSG',
          snackMessage: {
            message: 'Trigger Updated',
            level: 'success',
            duration: 3000
          }
        });
        dispatch({
          type: 'ACTION_SET_IS_LOADING_MODULE',
          module: 'removeKeywordTrigger',
          loading: false
        });
      })
      .catch((error) => {
        dispatch({
          type: 'ACTION_SET_SNACK_MSG',
          snackMessage: {
            message: error.message || 'An error occurred. Please try again.',
            level: 'error',
            duration: 3000
          }
        });
        dispatch({
          type: 'ACTION_SET_IS_LOADING_MODULE',
          module: 'removeKeywordTrigger',
          loading: false
        });
        setKeyword(null);
      });
  };

  return (
    <div>
      {/* "Add Keyword" button */}
      <Button
        sx={botSettingsContentStyles.submitBtn(theme)}
        color="primary"
        variant="contained"
        type="button"
        onClick={() => {
          setIsNewKeyword(true);
          setOpenEditKeywordDialog(true);
          log('adding new keyword...');
        }}
      >
        {`Add ${i18n.t('Keyword')}`}
      </Button>

      {/* Display existing keywords if any */}
      {keywords && keywords.length > 0 && (
        keywords.map((kwd, idx) => {
          const isInactive = !kwd.IsActive;
          return (
            <Paper
              key={`kwd-${idx}`}
              square
              sx={{
                ...botSettingsContentStyles.keywordRow(theme),
                ...(isInactive ? botSettingsContentStyles.keywordRowOFF(theme) : {})
              }}
            >
              <Grid container wrap="nowrap">
                {/* Switch column */}
                <div style={botSettingsContentStyles.colSwitch(theme)}>
                  <Switch
                    checked={kwd.IsActive}
                    color="primary"
                    inputProps={{ 'aria-label': 'toggle keyword active' }}
                    onChange={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleSwitchChange(e, kwd);
                    }}
                  />
                </div>

                {/* Keyword text column */}
                <div style={{
                  ...botSettingsContentStyles.colKeyword(theme),
                  ...(isInactive ? botSettingsContentStyles.keywordRowOFF(theme) : {})
                }}>
                  <Typography
                    variant="body1"
                    sx={botSettingsContentStyles.colKeywordContent(theme)}
                  >
                    {kwd.Keyword}
                    {isInactive && <i>&nbsp;{'(Paused)'}</i>}
                  </Typography>
                </div>

                {/* Last run date column */}
                <div style={{
                  ...botSettingsContentStyles.colTimestamp(theme),
                  ...(isInactive ? botSettingsContentStyles.keywordRowOFF(theme) : {})
                }}>
                  {kwd.LastRunDt && (
                    moment().diff(kwd.LastRunDt, 'days') > 1
                      ? (
                        <Typography variant="body1">
                          {`Last run on ${moment(kwd.LastRunDt).format('L')}`}
                        </Typography>
                      ) : (
                        <Typography variant="body1">
                          {`Last run ${moment(kwd.LastRunDt).from(moment())}`}
                        </Typography>
                      )
                  )}
                </div>

                {/* Edit icon column */}
                <div style={{
                  ...botSettingsContentStyles.colEditIcon(theme),
                  ...(isInactive ? botSettingsContentStyles.keywordRowOFF(theme) : {})
                }}>
                  <IconButton
                    aria-label="Edit keyword"
                    onClick={() => setKeyword(kwd)}
                  >
                    <EditIcon />
                  </IconButton>
                </div>
              </Grid>
            </Paper>
          );
        })
      )}
    </div>
  );
};

export default KeywordTriggersRow;